import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MenuVisibilityService {
  private readonly lastAppRouteKey = 'lastAppRoute';

  private nonMenuRoutes: string[] = [
    '/login',
    '/register',
    '/plans',
    '/register-existing-user',
    '/invite-register',
    '/privacy-policy',
    '/terms-of-service',
    '/support',
    '/reset-password',
    '/reset-password-request',
    '/forgot-username',
    '/'
  ];

  // Expose current visibility + normalizedUrl for consumers
  private stateSubject = new BehaviorSubject<{ showMenu: boolean; normalizedUrl: string }>({ showMenu: true, normalizedUrl: '/' });
  public state$ = this.stateSubject.asObservable();

  constructor(private router: Router) {
    // Initialize from current URL
    try {
      const url = this.router.url || '/';
      this.updateForUrl(url);
    } catch (e) {
      this.stateSubject.next({ showMenu: true, normalizedUrl: '/' });
    }

    // Update on navigation end
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((ev) => {
      this.updateForUrl(ev.urlAfterRedirects || ev.url);
    });
  }

  private normalize(url: string): string {
    let normalized = (url || '').split('?')[0].split('#')[0];
    if (!normalized) {
      normalized = '/';
    }
    if (normalized.length > 1 && normalized.endsWith('/')) normalized = normalized.slice(0, -1);
    return normalized;
  }

  private updateForUrl(url: string) {
    const normalized = this.normalize(url);
    const isNonMenu = this.nonMenuRoutes.some(r => r === normalized || normalized.startsWith(r + '/'));

    // Persist the latest app route so we can restore deep links if a hard refresh
    // is rewritten to '/' by hosting/proxy infrastructure.
    if (!isNonMenu && normalized !== '/') {
      try {
        sessionStorage.setItem(this.lastAppRouteKey, normalized);
      } catch (e) {
        // Ignore storage access failures (private mode, disabled storage, etc.).
      }
    }

    this.stateSubject.next({ showMenu: !isNonMenu, normalizedUrl: normalized });
  }

  /**
   * Returns the most recent in-app route that uses the shell layout.
   */
  public getLastAppRoute(): string | null {
    try {
      const route = sessionStorage.getItem(this.lastAppRouteKey);
      return route && route !== '/' ? route : null;
    } catch (e) {
      return null;
    }
  }

  public refreshForCurrentUrl(): void {
    this.updateForUrl(this.router.url || '/');
  }

  public isNonMenu(url: string): boolean {
    const normalized = this.normalize(url);
    return this.nonMenuRoutes.some(r => r === normalized || normalized.startsWith(r + '/'));
  }
}
